import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Copy, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { fmtLog } from "@/lib/tz";

const REC = {
  Approve: { c: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  Review: { c: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  Reject: { c: "text-red-700 bg-red-50 border-red-200", Icon: XCircle },
};
const FLAG = { pass: "text-emerald-600", warn: "text-amber-600", fail: "text-red-600" };

export function AiReviewBadge({ rec, confidence }) {
  if (!rec) return null;
  const r = REC[rec] || REC.Review;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${r.c}`}>
      <Sparkles className="h-2.5 w-2.5" />{rec}{confidence != null ? ` ${confidence}%` : ""}
    </span>
  );
}

export function AiAutoBadge({ action }) {
  const a = action || "";
  if (a === "would_auto_approve")
    return <span data-testid="ai-auto-badge" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border text-indigo-700 bg-indigo-50 border-indigo-200"><Zap className="h-2.5 w-2.5" />Shadow: would approve</span>;
  if (a === "auto_approved")
    return <span data-testid="ai-auto-badge" className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200"><Zap className="h-2.5 w-2.5" />Auto-approved</span>;
  if (a.startsWith("blocked:"))
    return <span data-testid="ai-auto-badge" title={a} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border text-amber-700 bg-amber-50 border-amber-200"><Zap className="h-2.5 w-2.5" />Auto-approve blocked</span>;
  return null;
}

export function AiReviewPanel({ name, ai, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(ai || {});
  useEffect(() => { setData(ai || {}); }, [ai, name]);

  const flags = (() => {
    try {
      if (Array.isArray(data.flags)) return data.flags;
      return typeof data.ai_flags === "string" ? JSON.parse(data.ai_flags || "[]") : (data.ai_flags || []);
    } catch { return []; }
  })();
  const rec = data.ai_recommendation;

  const run = async () => {
    setBusy(true);
    try { const r = await api.aiReview(name); setData(r); onRefreshed?.(); toast.success("AI review updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "AI review failed"); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(data.ai_note_draft || "").catch(() => {}); toast.success("Drafted note copied"); };

  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3 text-xs" data-testid="ai-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 flex items-center gap-1"><Sparkles className="h-3 w-3" />AI review</div>
        <div className="flex items-center gap-2">
          {rec && <AiReviewBadge rec={rec} confidence={data.ai_confidence} />}
          <AiAutoBadge action={data.ai_auto_action} />
          <button onClick={run} disabled={busy} className="text-indigo-600 hover:text-indigo-800" data-testid="ai-panel-run" title="Run / re-run">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {!rec ? (
        <div className="text-slate-400 mt-1">Not yet reviewed. <button onClick={run} className="text-indigo-600 font-semibold" data-testid="ai-panel-run-empty">Run AI review</button></div>
      ) : (
        <div className="mt-2 space-y-2">
          {data.ai_summary && <div className="text-slate-700">{data.ai_summary}</div>}
          {data.ai_note_review && (
            <div className="rounded bg-white border border-slate-200 p-2">
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Office should be aware</div>
              <div className="text-slate-700 mt-0.5">{data.ai_note_review}</div>
            </div>
          )}
          {data.ai_hours_basis && (
            <div className="rounded bg-white border border-slate-200 p-2" data-testid="ai-hours-basis">
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">How the hours were judged</div>
              <div className="text-slate-700 mt-0.5">{data.ai_hours_basis}</div>
            </div>
          )}
          {flags.length > 0 && (
            <div className="space-y-0.5">
              {flags.map((f, i) => (
                <div key={i} className="flex gap-1.5" data-testid={`ai-flag-${i}`}>
                  <span className={`font-bold ${FLAG[f.status] || "text-slate-500"}`}>{(f.status || "").toUpperCase()}</span>
                  <span className="text-slate-600"><b>{f.criterion}:</b> {f.detail}</span>
                </div>
              ))}
            </div>
          )}
          {data.ai_note_draft && (
            <div className="rounded bg-white border border-slate-200 p-2">
              <div className="flex items-center justify-between">
                <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Drafted approval note</div>
                <button onClick={copy} className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-[10px] font-semibold" data-testid="ai-copy-note"><Copy className="h-3 w-3" />Copy</button>
              </div>
              <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{data.ai_note_draft}</div>
            </div>
          )}
          {data.ai_reviewed_at && <div className="text-[10px] text-slate-400">{data.ai_model} · {fmtLog(data.ai_reviewed_at)}</div>}
        </div>
      )}
    </div>
  );
}
